import { Entypo } from "@expo/vector-icons";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import Button from "components/Button";
import WhiteText from "components/WhiteText";
import { Image } from "expo-image";
import { setSelectedSubscription } from "features/slices/reservationFlowSlice";
import {
  RedemptionLimitDetails,
  SubscriptionDetails,
  TicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import fill from "lodash/fill";
import { MainFlowParamList } from "navigation/types";
import i18n from "plugins/localization";
import React, { useCallback } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { formatEventDate } from "utils/eventUtils";
import { svgURL } from "utils/fileUtils";
import { toScehdule } from "utils/navigationUtils";

const SLOT_SQUARE_SIZE = 50;
const LOGO_SIZE = 40;

// https://github.com/FanRally/fanrally-core/issues/1200
const UNLIMITED_SLOTS = -1;

const ReservationDetails = (props: {
  memberReservationSubs: SubscriptionDetails[];
}) => {
  const { memberReservationSubs } = props;

  const { account, accountEvents = [], redemptionLimits = [] } = useAccount();
  const dispatch = useAppDispatch();
  const { tenantSecondaryColor } = useAppSelector((state) => state.tenant);
  const navigation = useNavigation<NavigationProp<MainFlowParamList>>();
  const getRemainingReservationSlotsForMember = useCallback(
    (memberSub: SubscriptionDetails) => {
      const currentTickets = memberSub.currentTickets;
      const total = memberSub.planPackage.primaryComponent.quantity;
      return {
        used: currentTickets,
        // just in case current tickets is greater than total, which is rare
        remaining: Math.max(0, total - currentTickets),
        total,
      };
    },
    [account]
  );

  const ticketsBelongToCurrentSub = (sub: SubscriptionDetails) => {
    return accountEvents.reduce<TicketDetails[]>((result, accountEvent) => {
      accountEvent.tickets.forEach((ticket) => {
        if (
          ticket.user &&
          ticket.user.id === sub.member.user.id &&
          ticket.planPackageId === sub.planPackage.id
        ) {
          result.push(ticket);
        }
      });
      return result;
    }, []);
  };

  const getGroupCodeByEventCode = (eventCode: string) => {
    return accountEvents.find(
      (accountEvent) => accountEvent.event.code === eventCode
    )?.groupCode.groupCode!;
  };

  const getRedemptionLimitBySubId = (subId: number) => {
    return redemptionLimits.find(
      (redemptionLimit) => redemptionLimit.subscriptionId === subId
    );
  };

  const getNumberOfGamesUsed = (
    redemptionLimitDetails: RedemptionLimitDetails
  ) => {
    const { numRedeemed, redemptionLimit, redemptionLimitPeriod } =
      redemptionLimitDetails;
    const params = {
      redeemed: numRedeemed,
      limit: redemptionLimit,
    };
    let content = "";

    if (redemptionLimitPeriod === "MONTH") {
      content = i18n.t("screens.Dashboard.redemptionUsageMonth", params);
    } else if (redemptionLimitPeriod === "SEASON") {
      content = i18n.t("screens.Dashboard.redemptionUsageSeason", params);
    }
    return <WhiteText category="p2">{content}</WhiteText>;
  };
  return (
    <View style={{ marginTop: 5 }}>
      {memberReservationSubs.length > 0 && (
        <View>
          {memberReservationSubs.map((sub) => {
            const { remaining } = getRemainingReservationSlotsForMember(sub);

            const redemptionLimit = getRedemptionLimitBySubId(sub.id);
            const quantity = sub.planPackage.primaryComponent.quantity;

            return (
              <View
                key={sub.id}
                className="border border-white rounded-xl p-4 mb-4"
              >
                <WhiteText category="h6">{sub.planPackage.name}</WhiteText>

                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  {/**For auto reserve plans, do not show reserved game tiles */}
                  {!sub.planPackage.autoReserveEnabled &&
                    ticketsBelongToCurrentSub(sub).map((ticket) => (
                      <View key={ticket.id} style={styles.slotSquareContainer}>
                        <Pressable
                          onPress={() => {
                            navigation.navigate("MyEvents", {
                              screen: "EventDetail",
                              params: {
                                groupCode: getGroupCodeByEventCode(
                                  ticket.event.code
                                ),
                              },
                            });
                          }}
                          style={[
                            styles.slotSquare,
                            {
                              borderColor: tenantSecondaryColor,
                            },
                          ]}
                        >
                          <View>
                            <Image
                              source={{
                                uri: svgURL(
                                  ticket.event.type === "EXPERIENCE"
                                    ? ticket.event.hostName
                                    : ticket.event.opponentName,
                                  false
                                ),
                              }}
                              style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
                              responsivePolicy="initial"
                              contentFit="contain"
                            />
                          </View>
                        </Pressable>
                        {/* event date mask */}
                        <View>
                          <WhiteText
                            category="c2"
                            style={{ textAlign: "right" }}
                          >
                            {formatEventDate(ticket.event, "M/D")}
                          </WhiteText>
                        </View>
                      </View>
                    ))}

                  {quantity !== UNLIMITED_SLOTS &&
                    fill(Array(remaining), 0).map((_, index) => (
                      <View style={styles.slotSquareContainer} key={index}>
                        <Pressable
                          testID="reservation-empty-card"
                          onPress={() => {
                            dispatch(setSelectedSubscription(sub));
                            navigation.navigate("Schedule", {
                              screen: "ScheduleScreen",
                            });
                          }}
                          style={styles.slotSquare}
                        >
                          <Entypo name="plus" size={24} color="#cccccc" />
                        </Pressable>
                      </View>
                    ))}
                </View>

                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <WhiteText category="p2">
                    {quantity !== UNLIMITED_SLOTS &&
                      ((
                        <>
                          {
                            i18n.t("screens.Dashboard.reservationLimit", {
                              slots: sub.planPackage.primaryComponent.quantity,
                            }) as string
                          }{" "}
                          {
                            (redemptionLimit !== undefined &&
                              getNumberOfGamesUsed(
                                redemptionLimit!
                              )) as React.ReactElement
                          }
                        </>
                      ) as any)}
                  </WhiteText>
                  {sub.planPackage.ctaButtonText && (
                    <Button
                      appearance="outline"
                      style={{ alignSelf: "flex-end" }}
                      onPress={() => {
                        dispatch(setSelectedSubscription(sub));
                        toScehdule();
                      }}
                      size="small"
                    >
                      {sub.planPackage.ctaButtonText}
                    </Button>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  lineContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  lineText: {
    marginRight: 5,
  },
  slotSquareContainer: {
    alignItems: "center",
    marginRight: 6,
    marginBottom: 6,
  },
  slotSquare: {
    width: SLOT_SQUARE_SIZE,
    height: SLOT_SQUARE_SIZE,
    borderRadius: 8,
    backgroundColor: "white",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
  },
});
export default ReservationDetails;
